import { OverlayShowDirection, OverlayStyleType } from "@obsbot/remo-stores";
import { throttle } from "lodash-es";
import React, {
    ReactElement,
    useCallback,
    useRef,
    MouseEvent,
    useEffect,
    WheelEvent,
    memo,
    forwardRef,
    useImperativeHandle,
} from "react";
import { OverlayAvatarCom } from "remo-components";
import DefaultAvatar2PNG from "../avatars/default_2.png";
import DefaultAvatar3PNG from "../avatars/default_3.png";
import DefaultAvatar1PNG from "../avatars/default_1.png";
import DefaultAvatar4PNG from "../avatars/default_4.png";
import DefaultAvatar5PNG from "../avatars/default_5.png";

// 边缘检测的阈值（像素）
const EDGE_THRESHOLD = 20;
// 调整大小的敏感区域（像素）
const RESIZE_THRESHOLD = 14;

interface AvatarListComProps {
    position: { x: number; y: number };
    setPosition: (position: { x: number; y: number }) => void;
    containerRef: React.RefObject<HTMLDivElement | null>;
    size: number;
    opacity: number;
    curShowDirection: OverlayShowDirection;
    isChangeSize: boolean;
    overlayStyleType: OverlayStyleType;
    updateOpacity: (opacity: number) => void;
    updateSize: (size: number) => void;
}

interface DragPosition {
    x: number;
    y: number;
    corner: string;
}

type DirectionConfig = {
    maxX: number;
    maxY: number;
    minX: number;
    minY: number;
};

export type AvatarListRef = HTMLDivElement | null;

const defaultAvatarShow = [
    DefaultAvatar2PNG,
    DefaultAvatar3PNG,
    DefaultAvatar1PNG,
    DefaultAvatar4PNG,
    DefaultAvatar5PNG,
];

const AvatarListCom = forwardRef<AvatarListRef, AvatarListComProps>(
    (
        {
            containerRef,
            position,
            setPosition,
            curShowDirection,
            size,
            opacity,
            isChangeSize,
            overlayStyleType,
            updateOpacity,
            updateSize,
        },
        ref,
    ): ReactElement => {
        const isDragging = useRef(false);
        const isResizing = useRef(false);
        const dragStartPos = useRef<DragPosition>({ x: 0, y: 0, corner: "" });
        const startSize = useRef(size);
        const avatarRef = useRef<HTMLDivElement>(null);
        const isFirstIn = useRef(true);

        useImperativeHandle(ref, () => {
            if (!avatarRef.current) {
                throw new Error("avatarRef is not mounted yet");
            }
            return avatarRef.current;
        });

        // 处理鼠标滚轮事件
        const handleWheel = useCallback(
            (e: WheelEvent<HTMLDivElement>) => {
                const delta = e.deltaY > 0 ? -10 : 10;
                updateOpacity(Math.min(100, Math.max(10, opacity + delta)));
            },
            [opacity, updateOpacity],
        );

        // 检查是否在调整大小的区域
        const isInResizeArea = useCallback(
            (e: MouseEvent<HTMLDivElement>): { isResize: boolean; corner: string } => {
                if (!containerRef.current) {
                    return { isResize: false, corner: "" };
                }
                const handles = containerRef.current.querySelectorAll(".resize-handle");
                for (const handle of handles) {
                    const rect = handle.getBoundingClientRect();
                    const isInHandle =
                        e.clientX >= rect.left &&
                        e.clientX <= rect.right &&
                        e.clientY >= rect.top &&
                        e.clientY <= rect.bottom;

                    if (isInHandle) {
                        const corner = handle.classList.contains("nw")
                            ? "nw-resize"
                            : handle.classList.contains("ne")
                              ? "ne-resize"
                              : handle.classList.contains("sw")
                                ? "sw-resize"
                                : "se-resize";
                        return { isResize: true, corner };
                    }
                }

                return { isResize: false, corner: "" };
            },
            [containerRef],
        );

        // 处理鼠标按下
        const handleMouseDown = (e: MouseEvent<HTMLDivElement>): void => {
            const resizeCheck = isInResizeArea(e);
            if (resizeCheck.isResize) {
                isResizing.current = true;
                startSize.current = size;
                dragStartPos.current = {
                    x: e.clientX,
                    y: e.clientY,
                    corner: resizeCheck.corner,
                };
                e.currentTarget.style.cursor = resizeCheck.corner;
            } else {
                isDragging.current = true;
                const rect = e.currentTarget.getBoundingClientRect();
                dragStartPos.current = {
                    x: e.clientX - rect.left,
                    y: e.clientY - rect.top,
                    corner: "",
                };
                e.currentTarget.style.cursor = "grabbing";
            }
        };

        // 通过摆放方式判断是否超出屏幕
        const getDirectionBounds = useCallback(
            (containerRect: DOMRect, elementRect: DOMRect): DirectionConfig => {
                const effectiveWidth = containerRect.width;
                const effectiveHeight = containerRect.height;
                // const avatarCom = avatarRef.current?.children[0];
                // const rect = avatarCom?.getBoundingClientRect();

                const adjustedWidth = elementRect.width;
                // overlayStyleType === OverlayStyleType.Tab
                //     ? elementRect.width
                //     : elementRect.width + rect!.width * 0.64;

                switch (curShowDirection) {
                    case OverlayShowDirection.Vertical: {
                        return {
                            maxX: effectiveWidth - adjustedWidth,
                            maxY: effectiveHeight - elementRect.height,
                            minX: 0,
                            minY: 0,
                        };
                    }
                    case OverlayShowDirection.Horizontal: {
                        return {
                            maxX: effectiveWidth - adjustedWidth,
                            maxY: effectiveHeight - elementRect.height,
                            minX: 0,
                            minY: 0,
                        };
                    }
                    default: {
                        return {
                            maxX: effectiveWidth - adjustedWidth,
                            maxY: effectiveHeight - elementRect.height,
                            minX: 0,
                            minY: 0,
                        };
                    }
                }
            },
            [curShowDirection],
        );

        // 检查边缘并返回吸附后的位置
        const checkEdgeSnap = useCallback(
            (x: number, y: number, containerRect: DOMRect, elementRect: DOMRect) => {
                const bounds = getDirectionBounds(containerRect, elementRect);

                // 确保位置在有效范围内
                let snappedX = Math.max(bounds.minX, Math.min(bounds.maxX, x));
                let snappedY = Math.max(bounds.minY, Math.min(bounds.maxY, y));

                // 计算边缘距离
                const leftDist = x;
                const rightDist = containerRect.width - (x + elementRect.width);
                const topDist = y;
                const bottomDist = containerRect.height - (y + elementRect.height);

                // 水平方向吸附
                if (leftDist < EDGE_THRESHOLD) {
                    snappedX = bounds.minX;
                } else if (rightDist < EDGE_THRESHOLD) {
                    snappedX = bounds.maxX;
                }

                // 垂直方向吸附
                if (topDist < EDGE_THRESHOLD) {
                    snappedY = bounds.minY;
                } else if (bottomDist < EDGE_THRESHOLD) {
                    snappedY = bounds.maxY;
                }

                return { snappedX, snappedY };
            },
            [getDirectionBounds],
        );

        // 使用throttle来节流位置更新
        const updatePosition = useRef(
            throttle((newX: number, newY: number) => {
                if (!containerRef.current || !avatarRef.current) {
                    return;
                }

                const containerRect = containerRef.current.getBoundingClientRect();
                const elementRect = avatarRef.current.getBoundingClientRect();

                // 计算新位置(考虑鼠标在元素内的偏移)
                const rawX = newX - containerRect.left - dragStartPos.current.x;
                const rawY = newY - containerRect.top - dragStartPos.current.y;

                // 应用边缘吸附
                const { snappedX, snappedY } = checkEdgeSnap(
                    rawX,
                    rawY,
                    containerRect,
                    elementRect,
                );

                // 直接使用像素值
                const newPosition = {
                    x: snappedX,
                    y: snappedY,
                };
                setPosition(newPosition);
            }, 16),
        ).current;

        // 使用throttle来节流大小更新
        const updateSizeThrottled = useCallback(
            throttle((e: globalThis.MouseEvent) => {
                if (!avatarRef.current || !isResizing.current) {
                    return;
                }
                const deltaX = e.clientX - dragStartPos.current.x;
                const deltaY = e.clientY - dragStartPos.current.y;
                let delta = 0;

                // 根据不同角落计算大小变化
                switch (dragStartPos.current.corner) {
                    case "nw-resize": {
                        // 向外（左上）拖动时增大，向内（右下）拖动时减小 // 左上角
                        delta =
                            Math.max(Math.abs(deltaX), Math.abs(deltaY)) *
                            (deltaX + deltaY < 0 ? 1 : -1);
                        break;
                    }
                    case "ne-resize": {
                        // 向外（右上）拖动时增大，向内（左下）拖动时减小 // 右上角
                        delta =
                            Math.max(Math.abs(deltaX), Math.abs(deltaY)) *
                            (deltaX - deltaY > 0 ? 1 : -1);
                        break;
                    }
                    case "sw-resize": {
                        // 向外（左下）拖动时增大，向内（右上）拖动时减小 // 左下角
                        delta =
                            Math.max(Math.abs(deltaX), Math.abs(deltaY)) *
                            (-deltaX + deltaY > 0 ? 1 : -1);
                        break;
                    }
                    case "se-resize": {
                        // 向外（右下）拖动时增大，向内（左上）拖动时减小 // 右下角
                        delta =
                            Math.max(Math.abs(deltaX), Math.abs(deltaY)) *
                            (-deltaX - deltaY > 0 ? -1 : 1);
                        break;
                    }
                }

                // 计算新的大小，限制在10-100之间
                const minSize = 10;
                const newSize = Math.min(100, Math.max(minSize, startSize.current + delta * 0.5));
                updateSize(newSize);
                if (avatarRef.current) {
                    avatarRef.current.style.borderRadius = `${avatarRef.current.clientHeight / 2}px`;
                }
            }, 16),
            [curShowDirection, overlayStyleType],
        );

        useEffect(() => {
            if (avatarRef.current) {
                avatarRef.current.style.borderRadius = `${avatarRef.current.clientHeight / 2}px`;
            }
        }, [size]);

        useEffect(() => {
            const handleMouseMove = (e: globalThis.MouseEvent): void => {
                if (isResizing.current) {
                    updateSizeThrottled(e);
                } else if (isDragging.current) {
                    updatePosition(e.clientX, e.clientY);
                }
            };

            const handleMouseUp = (): void => {
                if (isResizing.current) {
                    // 在调整大小结束时检查边界
                    if (containerRef.current && avatarRef.current) {
                        const containerRect = containerRef.current.getBoundingClientRect();
                        const elementRect = avatarRef.current.getBoundingClientRect();

                        // 使用当前位置检查边界
                        const { snappedX, snappedY } = checkEdgeSnap(
                            position.x,
                            position.y,
                            containerRect,
                            elementRect,
                        );

                        // 如果位置需要调整，更新位置
                        if (
                            Math.abs(position.x - snappedX) > 0.1 ||
                            Math.abs(position.y - snappedY) > 0.1
                        ) {
                            setPosition({
                                x: snappedX,
                                y: snappedY,
                            });
                        }
                    }
                }
                isDragging.current = false;
                isResizing.current = false;
            };

            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);

            return () => {
                document.removeEventListener("mousemove", handleMouseMove);
                document.removeEventListener("mouseup", handleMouseUp);
            };
        }, [
            updatePosition,
            updateSizeThrottled,
            position,
            checkEdgeSnap,
            setPosition,
            containerRef,
        ]);

        // 改变完大小后判断是否超出边缘
        useEffect(() => {
            if (!isChangeSize && !isDragging.current && !isResizing.current) {
                // 当尺寸修改完成后，检查是否超出边缘
                if (containerRef.current && avatarRef.current) {
                    const containerRect = containerRef.current.getBoundingClientRect();
                    const elementRect = avatarRef.current.getBoundingClientRect();

                    // 直接使用像素值
                    const { snappedX, snappedY } = checkEdgeSnap(
                        position.x,
                        position.y,
                        containerRect,
                        elementRect,
                    );

                    if (
                        Math.abs(position.x - snappedX) > 0.1 ||
                        Math.abs(position.y - snappedY) > 0.1
                    ) {
                        if (isFirstIn.current) {
                            isFirstIn.current = false;
                            return;
                        }
                        setPosition({
                            x: snappedX,
                            y: snappedY,
                        });
                    }
                }
            }
        }, [isChangeSize, position, checkEdgeSnap, setPosition, containerRef]);

        const avatarList = useRef(
            new Array(5).fill(0).map((_, index) => {
                return {
                    avatarUrl: defaultAvatarShow[index],
                    isSpeak: index > 1,
                    isTurnOnMic: false,
                    networkQuality: 0,
                    showAnimations: [],
                    isTurnOnCamera: false,
                    isShowAnimations: false,
                    rtcAvatar: null,
                };
            }),
        );

        return (
            <div
                ref={avatarRef}
                className={`avatarList no-drag ${curShowDirection} ${overlayStyleType}`}
                style={{
                    left: `${position.x}px`,
                    top: `${position.y}px`,
                    opacity: opacity / 100,
                    gap: 20,
                    cursor: "grab",
                    borderRadius: size,
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={e => {
                    const resizeCheck = isInResizeArea(e);
                    if (resizeCheck.isResize) {
                        e.currentTarget.style.cursor = resizeCheck.corner;
                    } else {
                        e.currentTarget.style.cursor = isDragging.current ? "grabbing" : "grab";
                    }
                }}
                onWheel={handleWheel}
            >
                {avatarList.current.map(item => (
                    <OverlayAvatarCom
                        isSettingPage={true}
                        overlayShowDirection={curShowDirection}
                        overlayShowStyle={overlayStyleType}
                        size={size}
                        {...item}
                        key={item.avatarUrl}
                    />
                ))}
                {/* 左上角 */}
                <div
                    className="resize-handle nw"
                    style={{
                        position: "absolute",
                        left: 18,
                        top: 0,
                        width: RESIZE_THRESHOLD,
                        height: RESIZE_THRESHOLD,
                        cursor: "nw-resize",
                    }}
                />
                {/* 右上角 */}
                <div
                    className="resize-handle ne"
                    style={{
                        position: "absolute",
                        right: 18,
                        top: 0,
                        width: RESIZE_THRESHOLD,
                        height: RESIZE_THRESHOLD,
                        cursor: "ne-resize",
                    }}
                />
                {/* 左下角 */}
                <div
                    className="resize-handle sw"
                    style={{
                        position: "absolute",
                        left: 18,
                        bottom: 0,
                        width: RESIZE_THRESHOLD,
                        height: RESIZE_THRESHOLD,
                        cursor: "sw-resize",
                    }}
                />
                {/* 右下角 */}
                <div
                    className="resize-handle se"
                    style={{
                        position: "absolute",
                        right: 18,
                        bottom: 0,
                        width: RESIZE_THRESHOLD,
                        height: RESIZE_THRESHOLD,
                        cursor: "se-resize",
                    }}
                />
            </div>
        );
    },
);

AvatarListCom.displayName = "AvatarListCom";

export default memo(AvatarListCom);
